import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpToLine, Box, Plus, Sparkles } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { confirmar } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { MarcoDeComercia } from '../components/marco-de-comercia';
import { EncabezadoDeComercia } from '../components/encabezado-de-comercia';
import { TablaDeProductos } from '../components/productos/tabla-de-productos';
import {
  DialogoDeProducto,
  type ProductoInicial,
} from '../components/productos/dialogo-de-producto';
import { SubirProforma } from '../components/productos/subir-proforma';
import { AsistenteArancelario } from '../components/productos/asistente-arancelario';
import { comerciaService } from '../services/comercia-service';
import { useImportacion } from '../store/importacion-store';
import type { DatosDeProducto, ProductoDeComercia } from '../types/comercia.types';
import { colorSiguiente } from '../utils/formatos';

type Pestana = 'productos' | 'proforma' | 'asistente';

/**
 * Comercia › Producto (maqueta de Melanie): lo que se quiere traer. Cuatro
 * pestañas: la lista, subir la proforma, agregar uno por uno y el asistente
 * arancelario. «Hasta aquí solo dime qué quieres traer, nada más.»
 */
export function ComerciaProductosPage() {
  const ubicacion = useLocation();
  const pestana: Pestana = ubicacion.pathname.endsWith('/proforma')
    ? 'proforma'
    : ubicacion.pathname.endsWith('/asistente')
      ? 'asistente'
      : 'productos';

  return (
    <MarcoDeComercia>
      <Contenido pestana={pestana} />
    </MarcoDeComercia>
  );
}

function Contenido({ pestana }: { pestana: Pestana }) {
  const { importacion, aplicar } = useImportacion();
  const navegar = useNavigate();
  const { toast } = useToast();
  const [dialogo, setDialogo] = useState<ProductoInicial | null>(null);
  const [abierto, setAbierto] = useState(false);

  const productos = importacion?.productos ?? [];
  const usados = productos.map((p) => p.color);

  const abrir = (inicial: ProductoInicial) => {
    setDialogo(inicial);
    setAbierto(true);
  };

  const guardar = async (datos: DatosDeProducto, productoId?: string) => {
    const r = productoId
      ? await comerciaService.editarProducto(productoId, datos)
      : await comerciaService.agregarProducto(datos);
    aplicar(r);
    setAbierto(false);
    toast({
      title: productoId ? 'Producto actualizado' : 'Producto agregado',
      description: datos.descripcion,
    });
    if (!productoId && pestana !== 'productos') navegar('/comercia/productos');
  };

  const quitar = async (p: ProductoDeComercia) => {
    const ok = await confirmar({
      title: `¿Quitar «${p.descripcion}»?`,
      description:
        p.ubicadas > 0
          ? 'También sale del contenedor donde estaba acomodado. Sigue en tu catálogo de Datos Maestros.'
          : 'Sale de esta importación. Sigue en tu catálogo de Datos Maestros.',
      confirmText: 'Quitar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      aplicar(await comerciaService.quitarProducto(p.id));
      toast({ title: 'Producto quitado', description: p.descripcion });
    } catch (e) {
      toast({
        title: 'No se pudo quitar',
        description: mensajeDeError(e, 'Vuelve a intentar.'),
        variant: 'destructive',
      });
    }
  };

  const confirmarProforma = async (lineas: DatosDeProducto[]) => {
    aplicar(await comerciaService.agregarLote(lineas));
    toast({
      title: lineas.length === 1 ? '1 producto agregado' : `${lineas.length} productos agregados`,
      description: 'Ya están en tu lista y en tu catálogo.',
    });
    navegar('/comercia/productos');
  };

  return (
    <>
      <EncabezadoDeComercia
        titulo="Productos"
        acumulado={
          importacion
            ? {
                total: importacion.resumen.acumulado,
                fob: importacion.resumen.fob,
                moneda: importacion.moneda,
              }
            : { total: 0, fob: 0 }
        }
      >
        <nav
          aria-label="Productos"
          className="flex items-end gap-x-8 overflow-x-auto border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <Pestana href="/comercia/productos" activa={pestana === 'productos'} icono={Box}>
            Productos
          </Pestana>
          <Pestana
            href="/comercia/productos/proforma"
            activa={pestana === 'proforma'}
            icono={ArrowUpToLine}
          >
            Subir proforma
          </Pestana>
          <button
            type="button"
            onClick={() => abrir({})}
            className="-mb-px inline-flex h-14 shrink-0 items-center gap-2 border-b-[3px] border-transparent text-[17px] font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-5 w-5" aria-hidden="true" />
            Agregar producto
          </button>
          <Pestana
            href="/comercia/productos/asistente"
            activa={pestana === 'asistente'}
            icono={Sparkles}
          >
            Asistente arancelario
          </Pestana>
        </nav>
      </EncabezadoDeComercia>

      {pestana === 'productos' &&
        (productos.length === 0 ? (
          <SinProductos onAgregar={() => abrir({})} />
        ) : (
          <>
            <TablaDeProductos
              productos={productos}
              resumen={importacion!.resumen}
              onEditar={(p) => abrir({ producto: p })}
              onQuitar={(p) => void quitar(p)}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <p>
                Los productos quedan también en Datos Maestros › Materiales: Cotiza los ofrece con
                su costo nacionalizado.
              </p>
              <Link
                to="/comercia/logistica/origen-destino"
                className={buttonVariants({ variant: 'degradado', size: 'lg', className: 'px-6' })}
              >
                Seguir con la logística
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </>
        ))}

      {pestana === 'proforma' && (
        <SubirProforma
          coloresUsados={usados}
          yaEnLaLista={productos.map((p) => p.descripcion)}
          onConfirmar={confirmarProforma}
        />
      )}

      {pestana === 'asistente' && (
        <AsistenteArancelario
          onElegir={(descripcion, partida) => abrir({ descripcion, partida })}
        />
      )}

      <DialogoDeProducto
        abierto={abierto}
        inicial={dialogo}
        colorSugerido={colorSiguiente(usados)}
        onCerrar={() => setAbierto(false)}
        onGuardar={guardar}
      />
    </>
  );
}

function Pestana({
  href,
  activa,
  icono: Icono,
  children,
}: {
  href: string;
  activa: boolean;
  icono: typeof Box;
  children: string;
}) {
  return (
    <Link
      to={href}
      aria-current={activa ? 'page' : undefined}
      className={cn(
        '-mb-px inline-flex h-14 shrink-0 items-center gap-2 border-b-[3px] text-[17px] font-semibold transition-colors duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        activa
          ? 'border-orange-700 text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      )}
    >
      <Icono className="h-5 w-5" aria-hidden="true" />
      {children}
    </Link>
  );
}

function SinProductos({ onAgregar }: { onAgregar: () => void }) {
  return (
    <section className="flex flex-col items-center rounded-tarjeta border border-dashed bg-card px-6 py-14 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-orange-700/10 text-orange-700">
        <Box className="h-7 w-7" aria-hidden="true" />
      </span>
      <h2 className="mt-4 font-titulo text-xl font-semibold">¿Qué quieres traer?</h2>
      <p className="mt-1 max-w-md text-[15px] text-muted-foreground">
        Sube la proforma del proveedor o agrega los productos uno por uno. Con eso armamos la carga,
        la ruta y el costo puesto en tu almacén.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/comercia/productos/proforma"
          className={buttonVariants({ variant: 'outline', size: 'lg', className: 'px-6' })}
        >
          <ArrowUpToLine className="h-4 w-4" aria-hidden="true" />
          Subir proforma
        </Link>
        <button
          type="button"
          onClick={onAgregar}
          className={buttonVariants({ variant: 'degradado', size: 'lg', className: 'px-6' })}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Agregar producto
        </button>
      </div>
    </section>
  );
}
